import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { ENTITY_REQUEST_ANSWER_TYPES } from '../../types/entity_add_requests';

// Identifies the exact contact values a duplicate lookup was last run for, so the same search isn't
// re-issued and an already-answered "did you mean" prompt isn't shown twice.
export const getDuplicateSearchKey = (values) =>
	`${nullSafeGet('companyName', values)}|${nullSafeGet('contactEmail', values)}`;

// Map a supplier facility returned by the duplicate lookup onto the contact fields of the supplier
// request form, so picking a match fills the request with that supplier's details.
export const getSupplierMatchContactDetails = (supplier) => ({
	companyName: nullSafeGet('displayName', supplier) || nullSafeGet('name', supplier),
	contactEmail:
		nullSafeGet('primaryContactEmail', supplier) || nullSafeGet('primaryContact.email', supplier),
	contactFirstName: nullSafeGet('primaryContact.nameGiven', supplier),
	contactLastName: nullSafeGet('primaryContact.nameFamily', supplier),
});

// Build the supplier `payload` from AntD form values. "Select All" means no id constraint, so
// the ids array is emptied when the corresponding toggle is on.
export const buildSupplierPayload = (values) => {
	const allProblemTypes = !!values.allProblemTypes;
	const allLocations = !!values.allLocations;
	return {
		companyName: values.companyName,
		contactEmail: values.contactEmail,
		contactFirstName: values.contactFirstName,
		contactLastName: values.contactLastName,
		allProblemTypes,
		problemTypeIds: allProblemTypes ? [] : nullSafeGetOrElse('problemTypeIds', values, []),
		allLocations,
		locationIds: allLocations ? [] : nullSafeGetOrElse('locationIds', values, []),
	};
};

// The API stores every answer as a string, whatever its question type — the same contract walkthrough
// task answers and troubleshooting answers use — so each input's natural value (a moment, a number, a
// yes/no boolean) is converted on the way out. Mirrors formatValueForBackendByInputType in
// TroubleshootingUtils.
const serializeAnswer = (question, rawAnswer) => {
	if (rawAnswer === undefined || rawAnswer === null) {
		return rawAnswer;
	}
	switch (question.answerType) {
		// A non-moment date value (a JS Date, or a string round-tripped from a stored answer) is
		// normalized through moment rather than stringified, so it can't reach the API as a locale
		// string like 'Mon Aug 18 2026 00:00:00 GMT+0000'. An unparseable value is dropped instead of
		// posting the literal 'Invalid date'.
		case ENTITY_REQUEST_ANSWER_TYPES.date: {
			const answerDate = moment.isMoment(rawAnswer) ? rawAnswer : moment(rawAnswer);
			return answerDate.isValid() ? answerDate.format('YYYY-MM-DD') : undefined;
		}
		// 'true' / 'false' for yes/no, matching the app-wide radio convention (cf. isTaskChecked).
		case ENTITY_REQUEST_ANSWER_TYPES.yes_no:
		case ENTITY_REQUEST_ANSWER_TYPES.number:
			return `${rawAnswer}`;
		default:
			return rawAnswer;
	}
};

// Rebuild the [{ question, answer, orderingId }] answer list from the nested `answers` form value.
export const buildAnswers = (questions, values) => {
	const answersByOrderingId = nullSafeGetOrElse('answers', values, {});
	return (questions || []).map((question) => ({
		question,
		answer: serializeAnswer(question, answersByOrderingId[String(question.orderingId)]),
		orderingId: question.orderingId,
	}));
};

// The inverse of serializeAnswer: turn the stored string back into the value its input expects, so
// editing a draft re-selects the yes/no radio and refills the number and date pickers. Mirrors
// formatForTroubleshootingAnswerInput in TroubleshootingUtils.
const deserializeAnswer = (question, storedAnswer) => {
	if (storedAnswer === undefined || storedAnswer === null || storedAnswer === '') {
		return undefined;
	}
	switch (question.answerType) {
		case ENTITY_REQUEST_ANSWER_TYPES.date:
			return moment(storedAnswer);
		case ENTITY_REQUEST_ANSWER_TYPES.number: {
			const answerNumber = parseFloat(storedAnswer);
			return Number.isNaN(answerNumber) ? undefined : answerNumber;
		}
		case ENTITY_REQUEST_ANSWER_TYPES.yes_no:
			return storedAnswer === 'true';
		default:
			return storedAnswer;
	}
};

// Render a stored answer for the read-only request summary. Answers are strings whatever the question
// type, so a "No" arrives as the string "false" — truthy in JS — and has to be compared rather than
// tested. nullSafeGetWithZero keeps any answer that is falsy in JS visible instead of collapsing it
// to "—".
export const getAnswerDisplayText = (answerEntry) => {
	const answer = nullSafeGetWithZero('answer', answerEntry);
	if (answer === undefined || answer === null || answer === '') {
		return '—';
	}
	if (nullSafeGet('question.answerType', answerEntry) === ENTITY_REQUEST_ANSWER_TYPES.yes_no) {
		return answer === 'true' ? 'Yes' : 'No';
	}
	return String(answer);
};

// Produce AntD form initialValues from an existing request payload + its answers, keyed by
// question orderingId so they line up with EntityRequestAnswerFields.
export const getSupplierRequestInitialValues = (record, questions) => {
	const payload = nullSafeGetOrElse('payload', record, {});
	const storedAnswers = nullSafeGetOrElse('answers', record, []);
	const answersByOrderingId = {};
	storedAnswers.forEach((answerEntry) => {
		const orderingId = nullSafeGet('orderingId', answerEntry);
		if (orderingId !== undefined && orderingId !== null) {
			answersByOrderingId[String(orderingId)] = answerEntry.answer;
		}
	});

	const answers = {};
	(questions || []).forEach((question) => {
		const key = String(question.orderingId);
		answers[key] = deserializeAnswer(question, answersByOrderingId[key]);
	});

	const problemTypeIds = nullSafeGetOrElse('problemTypeIds', payload, []);
	const locationIds = nullSafeGetOrElse('locationIds', payload, []);

	return {
		companyName: nullSafeGet('companyName', payload),
		contactEmail: nullSafeGet('contactEmail', payload),
		contactFirstName: nullSafeGet('contactFirstName', payload),
		contactLastName: nullSafeGet('contactLastName', payload),
		problemTypeIds,
		locationIds,
		allProblemTypes: nullSafeGetOrElse('allProblemTypes', payload, problemTypeIds.length === 0),
		allLocations: nullSafeGetOrElse('allLocations', payload, locationIds.length === 0),
		answers,
	};
};
